import React, { useState } from 'react';
import EditorUtils from '../../draft-js-plugins-utils/src';
import { css } from '@emotion/react';
import { DeleteIcon } from '../../../../../../images/Icons';
import { EditorState, SelectionState, Modifier } from 'draft-js';

import URLUtils from '$/shared/frontend/utils/urls/URLUtils';

import { EditorTheme } from '../../../EditorTheme';

import { defaultTheme } from '../theme';
import RadioOptions from '$/components/ui/Input/Basics/RadioOptions';
import { getMaxCharsMessage } from '$/utils/string';

const MAX_CHARS_BUTTON = 90;

export enum CTATypes {
  Register = 'Register',
  Custom = 'Custom',
}

export enum Alignment {
  left = 'left',
  center = 'center',
  right = 'right',
}

export const requiredMessage = 'This field is required.';
export const InvalidUrlMessage = 'Please enter a valid URL';
export const InputErrorMessage: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div
      className='ui red pointing basic label'
      css={css`
        position: absolute !important;
        display: table !important;
        top: 100%;
        left: 50%;
        margin-left: 0 !important;
        margin-top: 0.5rem !important;
        transform: translateX(-50%);
        z-index: 10;
        width: 100%;
        word-break: normal;
        span {
          display: block;
          margin-bottom: 0.25rem;
          &:last-child {
            margin-bottom: 0;
          }
        }
      `}
    >
      {<span>{message}</span>}
    </div>
  );
};

const updateAtSelection = (editorState: EditorState, text: string, url: string, align: string) => {
  const entityKey = EditorUtils.getCurrentEntityKey(editorState);
  const contentState = editorState.getCurrentContent();
  const newContentState = contentState.mergeEntityData(entityKey, {
    text: text,
    url: url,
    align: align,
  });
  let newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');

  const selection = editorState.getSelection();
  const anchorKey = selection.getAnchorKey();
  const anchorBlock = contentState.getBlockForKey(anchorKey);

  newEditorState = EditorState.forceSelection(
    newEditorState,
    selection.merge({
      anchorKey: anchorKey,
      focusKey: anchorKey,
      anchorOffset: anchorBlock.getText().length,
      focusOffset: anchorBlock.getText().length,
    })
  );

  return EditorState.forceSelection(
    newEditorState,
    newEditorState.getCurrentContent().getSelectionAfter()
  );
};

export const RemoveBlock = (props: any) => {
  const onRemoveButtonBlock = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    const { getEditorState, setEditorState } = props;

    const editorState = getEditorState();
    const selection = editorState.getSelection();

    let content = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const block = content.getBlockForKey(startKey);
    const linkKey = block.getEntityAt(startOffset);

    let contentWithRemovedBlock = content;
    block.findEntityRanges(
      //@ts-ignore
      (charData) => {
        const entityKey = charData.getEntity();
        if (!entityKey) return false;
        return entityKey === linkKey;
      }, //@ts-ignore
      (start, end) => {
        const entitySelection = new SelectionState({
          anchorKey: block.getKey(),
          focusKey: block.getKey(),
          anchorOffset: start,
          focusOffset: end,
        });
        contentWithRemovedBlock = Modifier.removeRange(content, entitySelection, 'forward');
        return;
      }
    );

    const newEditorState = EditorState.push(editorState, contentWithRemovedBlock, 'remove-range');
    setEditorState(newEditorState);

    props.onOverrideContent && props.onOverrideContent(undefined);
  };
  const buttonStyle =
    props.theme && props.theme.button ? props.theme.button : EditorTheme.buttonStyles.button;
  return (
    <button
      onClick={onRemoveButtonBlock}
      css={[
        css`
          margin-left: 0.125rem;
        `,
        buttonStyle,
      ]}
    >
      <DeleteIcon />
    </button>
  );
};

const singleFieldRegex = /^\s*{[^}]+}\s*/;

const ButtonBlockForm: React.FC<any> = (props) => {
  const entity = EditorUtils.getCurrentEntity(props.getEditorState());
  const data = entity ? entity.getData() : { text: '', url: '' };
  const [text, setText] = useState(data.text || 'Register now');
  const [url, setUrl] = useState(data.url);
  const [isInvalidTitle, setIsInvalidTitle] = useState(false);
  const [isInvalidUrl, setIsInvalidUrl] = useState(false);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorUrl, setErrorUrl] = useState<string | null>(null);
  const [ctaType, setCtaType] = React.useState<string>(
    data.url || props.onlyCustomCta ? CTATypes.Custom : CTATypes.Register
  );
  const [align, setAlign] = React.useState<string>(data.align || Alignment.center);
  const field = data.url ? data.url.match(singleFieldRegex) : null;
  const [isMergedField, setIsMergedField] = React.useState(
    typeof field !== 'undefined' && field !== null
  );

  const inputRef = React.useRef<HTMLInputElement>(null);
  const urlInputRef = React.useRef<HTMLInputElement>(null);

  const isUrl = (urlValue: string) => {
    if (props.validateUrl) {
      return props.validateUrl(urlValue);
    }

    return URLUtils.isUrl(urlValue);
  };

  const onMouseDown = (event: any) => {
    event.preventDefault();
  };

  const onClose = () => {
    props.onOverrideContent(undefined);
  };

  const onRequiredTitle = () => {
    setIsInvalidTitle(true);
    setErrorTitle(requiredMessage);
  };

  const onRequiredUrl = () => {
    setIsInvalidUrl(true);
    setErrorUrl(requiredMessage);
  };

  const onInvalidUrl = () => {
    setIsInvalidUrl(true);
    setErrorUrl(InvalidUrlMessage);
  };

  const isValidTitle = (): boolean => {
    if (!text || text === '') {
      onRequiredTitle();
      return false;
    }
    if (isInvalidTitle) return false;
    return true;
  };

  const isValidUrl = (): boolean => {
    if (!url || url === '') {
      onRequiredUrl();
      return false;
    }
    if (isInvalidUrl) return false;
    return true;
  };

  const onChangeTitle = (e: any) => {
    const newValue = e.target.value;
    if (!newValue || newValue === '') {
      onRequiredTitle();
    } else {
      const maxError = getMaxCharsMessage(MAX_CHARS_BUTTON, newValue);
      setErrorTitle(maxError);
      setIsInvalidTitle(Boolean(maxError));
    }

    setText(newValue);
  };

  const onChangeUrl = (e: any) => {
    const newValue = e.target.value;
    setUrl(newValue);
    const field = newValue ? newValue.match(singleFieldRegex) : null;
    if (typeof field !== 'undefined' && field !== null) {
      setIsMergedField(true);
    } else {
      setIsMergedField(false);
    }
    if (!newValue || newValue === '') {
      onRequiredUrl();
      return;
    }
    if (!URLUtils.isMail(URLUtils.normaliseMail(newValue))) {
      const normalizeUrl = URLUtils.normalizeUrl(newValue);
      const invalid = !isUrl(normalizeUrl);
      if (invalid) {
        onInvalidUrl();
        return;
      }
    }
    setIsInvalidUrl(false);
    setErrorUrl(null);
  };

  const submit = () => {
    const { getEditorState, setEditorState } = props;
    let customUrl = '';
    if (!isValidTitle()) return;
    if (ctaType === CTATypes.Custom) {
      if (!isValidUrl()) return;
      if (!URLUtils.isMail(URLUtils.normaliseMail(url))) {
        customUrl = URLUtils.normalizeUrl(url);
        if (!isUrl(customUrl)) {
          onInvalidUrl();
          return;
        }
      } else {
        customUrl = URLUtils.normaliseMail(url);
      }
    }
    setEditorState(updateAtSelection(getEditorState(), text, customUrl, align));
    inputRef && inputRef.current && inputRef.current.blur();
    urlInputRef && urlInputRef.current && urlInputRef.current.blur();
    //onClose();
  };

  const onSave = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    submit();
  };

  const onKeyDown = (e: any) => {
    if (ctaType === CTATypes.Custom) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      onClose();
    }
  };

  const onPasteTitle = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    let text = e.clipboardData.getData('text/plain');
    if (text && text !== '') {
      text = text.replace(/\n|\r/g, '');
      setText(text);
      setIsInvalidTitle(false);
    }
  };

  const onPasteUrl = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    let text = e.clipboardData.getData('text/plain');
    if (text && text !== '') {
      text = text.replace(/\n|\r/g, '');
      setUrl(text);
      setIsInvalidUrl(false);
    }
  };

  const inputStyle = isInvalidTitle
    ? [defaultTheme.input, defaultTheme.inputInvalid]
    : defaultTheme.input;
  const inputUrlStyle = isInvalidUrl
    ? [defaultTheme.input, defaultTheme.inputInvalid]
    : defaultTheme.input;

  return (
    <div css={defaultTheme.wrapper}>
      {!props.onlyCustomCta && (
        <div className='flex items-center field-group'>
          <label className='field-text w-5e'>Type:</label>
          <RadioOptions
            className='button-options'
            value={ctaType}
            options={[
              { key: CTATypes.Register, value: CTATypes.Register, title: 'Registration' },
              { key: CTATypes.Custom, value: CTATypes.Custom, title: 'Custom' },
            ]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setCtaType(e.target.value);
              props.onChangeButtonType && props.onChangeButtonType(e.target.value);
            }}
          />
        </div>
      )}
      <div className='flex items-center field-group'>
        <label className='field-text w-5e'>Align:</label>
        <RadioOptions
          className='button-options'
          value={align}
          options={[
            { key: Alignment.left, value: Alignment.left, title: 'Left' },
            { key: Alignment.center, value: Alignment.center, title: 'Center' },
            { key: Alignment.right, value: Alignment.right, title: 'Right' },
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setAlign(e.target.value);
            // props.onChangeButtonType && props.onChangeButtonType(e.target.value);
          }}
        />
      </div>
      <div className='flex'>
        <div className='flex flex-wrap w-full py-1/4e'>
          <div className='field-group'>
            <label className='field-text'>Title:</label>
            <div className='relative'>
              <input
                ref={inputRef}
                css={inputStyle}
                onChange={onChangeTitle}
                onKeyDown={onKeyDown}
                onPaste={onPasteTitle}
                value={text}
                placeholder='Register now'
              />
              {isInvalidTitle && errorTitle && <InputErrorMessage message={errorTitle} />}
            </div>
          </div>
          {ctaType === CTATypes.Custom && (
            <div className='field-group'>
              <label className='field-text'>URL:</label>
              <div className='relative'>
                <input
                  className={`url-input ${isMergedField ? 'is-merged-field' : ''}`}
                  ref={urlInputRef}
                  css={[
                    inputUrlStyle,
                    css`
                      &.is-merged-field::first-line {
                        background: #61a1dc;
                      }
                    `,
                  ]}
                  onChange={onChangeUrl}
                  onKeyDown={onKeyDown}
                  onPaste={onPasteUrl}
                  value={url}
                  placeholder='https://'
                />
                {isInvalidUrl && errorUrl && <InputErrorMessage message={errorUrl} />}
              </div>
            </div>
          )}
        </div>
        <div
          className='flex items-center justify-end w-6e mb-1/8e mt-auto'
          onMouseDown={onMouseDown}
        >
          <RemoveBlock {...props} />
          <button className='ml-1/4e' onClick={onSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ButtonBlockForm;
